// ── PLANS, CREDITS AND WHAT EACH ONE BUYS ────────────────────────────────────
// ONE source of truth. The pricing page, the limits enforced in the app, the
// search ranking and the Cloud Function that grants monthly credits all read
// from here — because a plan defined in four places is a plan that disagrees
// with itself the first time a number changes.
//
// NO NEW COLLECTION. A plan is one field on users/{uid}: `plan`, holding one of
// PLAN_IDS below. Credits are a second field, `credits`. That was the explicit
// requirement and it is the right shape — a plan is an attribute of an account,
// not an entity with a life of its own.
//
// ⚠️ THESE FIELDS MUST NOT BE CLIENT-WRITEABLE. users/{uid} currently allows its
// owner to write anything on the document, which means anyone can set
// `plan: 'gold'` and `credits: 999999` from the browser console in about ten
// seconds. Everything in this file is decoration until firestore.rules stops
// that and a Cloud Function becomes the only writer. See the note in
// firestore.rules.

// ── WHAT AN OPERATION COSTS ──────────────────────────────────────────────────
// One credit is pinned to the cheapest real operation, so a credit has a stable
// meaning in money: 1 credit = $0.04 of provider spend. Keeping the ratio here
// rather than in each caller means a provider price change is one edit.
export const CREDIT_COST_USD = 0.04;

export const COSTS = {
  // 3D scene → photoreal render. $0.04.
  render: 1,
  // A photograph → AI-staged room. $0.08, so it costs twice as much.
  staging: 2,
};

// What a brand-new account starts with. Four is a deliberate number: enough to
// see the product work twice over (two stagings, or four renders), not enough
// to be worth farming with throwaway accounts at $0.16 a time.
export const SIGNUP_CREDITS = 4;

export const PLAN_IDS = ['free', 'bronze', 'silver', 'gold'];

// `Infinity` rather than a large number or null: it compares correctly with <
// and >= everywhere a limit is checked, so no call site needs to special-case
// "unlimited" and none of them can forget to.
export const PLANS = {
  free: {
    id: 'free',
    name: 'Free',
    priceUsd: 0,
    monthlyCredits: 0,        // the 4 signup credits are a one-off, not a refill
    portfolioImages: 15,
    projects: 5,
    packDiscount: 0,
    // ── PLACEMENT ──
    // `searchRank` is a sort key, NOT a filter: a free designer still appears in
    // every search, just below the paid ones with the same relevance. A plan
    // that hides people outright would empty the directory that makes the site
    // worth visiting.
    searchRank: 0,
    recommended: false,       // eligible for the Recommended Designers rail
    topDesigner: false,       // eligible for the Top Designer badge
    homepageFeature: false,   // eligible for the homepage feature slot
    // TRUE ON EVERY TIER, including this one. Chat is not gated by plan
    // anywhere in the app — MessagesButton renders for every signed-in account
    // and no code path checks this flag — so a cross here advertised a
    // restriction that does not exist. See the row in Plans.jsx.
    messaging: true,          // client leads through chat
    prioritySupport: false,
    // How many banner images this tier may pin above its profile. 0 hides the
    // strip entirely, 1 is a single still, 3 is a carousel.
    announcements: 0,
    // ── PROMOTION ON OUR OWN SOCIAL ACCOUNTS ──
    // Posts per month on the Tunify Instagram and TikTok. A COUNT, not a flag,
    // because the tiers differ by how many rather than by whether — and 2 is
    // read as "2 or more" by the pricing page, which is the promise being made:
    // a floor, not a quota to be held to exactly.
    //
    // ⚠️ NOTHING IN THE APP SCHEDULES THESE. It is a manual commitment, and this
    // number is what the pricing page promises on your behalf. Changing it here
    // changes what paying designers are owed.
    socialPromos: 0,
  },
  bronze: {
    id: 'bronze',
    name: 'Bronze',
    priceUsd: 6.99,
    monthlyCredits: 45,
    portfolioImages: 35,
    projects: 12,
    packDiscount: 0.10,
    searchRank: 1,
    recommended: false,
    topDesigner: false,
    homepageFeature: false,
    messaging: true,
    prioritySupport: false,
    // How many banner images this tier may pin above its profile. 0 hides the
    // strip entirely, 1 is a single still, 3 is a carousel.
    announcements: 0,
    socialPromos: 0,
  },
  silver: {
    id: 'silver',
    name: 'Silver',
    priceUsd: 12.99,
    monthlyCredits: 120,
    portfolioImages: 60,
    projects: 30,
    packDiscount: 0.15,
    searchRank: 2,
    recommended: true,
    topDesigner: false,
    homepageFeature: false,
    messaging: true,
    prioritySupport: false,
    // How many banner images this tier may pin above its profile. 0 hides the
    // strip entirely, 1 is a single still, 3 is a carousel.
    announcements: 1,
    socialPromos: 1,          // once a month
    popular: true,            // the one the pricing page highlights
  },
  gold: {
    id: 'gold',
    name: 'Gold',
    priceUsd: 19.99,
    monthlyCredits: 200,
    portfolioImages: Infinity,
    projects: Infinity,
    packDiscount: 0.20,
    searchRank: 3,
    recommended: true,
    topDesigner: true,
    homepageFeature: true,
    messaging: true,
    prioritySupport: true,
    // How many banner images this tier may pin above its profile. 0 hides the
    // strip entirely, 1 is a single still, 3 is a carousel.
    announcements: 3,
    socialPromos: 2,          // 2 or more a month
  },
};

// ── BUYING CREDITS OUTRIGHT ──────────────────────────────────────────────────
// Packs, not a second subscription. Someone who needs 30 more renders this month
// is not asking to be billed every month forever, and unused credits from a pack
// are margin rather than an obligation to keep funding.
//
// `perCredit` is derived, never typed: it is what the pricing page shows as the
// unit price, and a hand-written copy of it would drift the moment a price moves.
export const CREDIT_PACKS = [
  { id: 'p5',   credits: 5,   priceUsd: 0.99 },
  { id: 'p15',  credits: 15,  priceUsd: 1.99 },
  { id: 'p35',  credits: 35,  priceUsd: 3.99 },
  { id: 'p60',  credits: 60,  priceUsd: 5.99 },
  { id: 'p100', credits: 100, priceUsd: 9.49, best: true },
].map(p => ({ ...p, perCredit: p.priceUsd / p.credits }));

/**
 * The plan record for a raw id, a user document, or a plan record itself.
 * Unknown or missing → free.
 *
 * IDEMPOTENT, and it has to be. Callers pass whichever of the three they happen
 * to hold, and a function that resolves a plan is expected to leave an
 * already-resolved one alone. It did not: a PLANS record carries `id`, not
 * `plan`, so feeding one back in read `undefined` and returned FREE. That is
 * what packPrice below did on every call — `planOf(planOf(x))` — which silently
 * zeroed the pack discount for bronze, silver and gold alike. No error, no
 * struck-through price, just everyone paying list price.
 *
 * The guard is an IDENTITY check, not a shape check: `PLANS[user.id] === user`
 * is true only for the actual objects in PLANS. A designer card also has an
 * `id` — their uid — and `PLANS[uid]` is undefined, so a card can never
 * accidentally satisfy it and falls through to its `plan` field as before.
 */
export function planOf(user) {
  if (user && PLANS[user.id] === user) return user;
  const id = typeof user === 'string' ? user : user?.plan;
  return PLANS[id] || PLANS.free;
}

/** What a pack costs this account, after their plan's discount. */
export function packPrice(pack, plan) {
  const off = planOf(plan).packDiscount;
  return Math.round(pack.priceUsd * (1 - off) * 100) / 100;
}

/**
 * Can this account afford `action`, and what would it leave?
 * Pure, so the paywall copy and the server check can agree without either
 * re-deriving the arithmetic.
 */
export function canAfford(credits, action) {
  const cost = COSTS[action] ?? 0;
  return { ok: (credits ?? 0) >= cost, cost, after: (credits ?? 0) - cost };
}

/**
 * Sort comparator putting higher plans first among designers of EQUAL
 * relevance. Returns 0 for same-plan pairs so the caller's existing order
 * survives — this ranks, it does not reorder the whole directory by money.
 */
export function byPlanRank(a, b) {
  return planOf(b).searchRank - planOf(a).searchRank;
}

/** Whether an account may use a placement slot at all. */
export const isEligible = (user, feature) => Boolean(planOf(user)[feature]);

// ── THE TEST ACCOUNT ─────────────────────────────────────────────────────────
// One address that may set its own plan and balance from the UI, so the paid
// tiers can be walked through before a payment provider exists.
//
// This constant only decides whether to SHOW the panel. The actual permission
// lives in firestore.rules (isTestAccount), keyed on the verified email inside
// the Firebase token — so editing this file, or the bundle it compiles to,
// grants nothing. Someone who changes it in devtools gets a form whose every
// write the database refuses.
//
// ⚠️ REMOVE THIS, ITS PANEL, AND isTestAccount IN firestore.rules BEFORE TAKING
// REAL MONEY. Grep for TEST_ACCOUNT_EMAILS and isTestAccount.
// A LIST, because there are two roles to exercise and one account can only be
// one of them: a designer account cannot walk the shop ladder, and a shop
// account cannot walk the designer one. Two addresses, one per role.
export const TEST_ACCOUNT_EMAILS = [
  'hnara15@gmail.com',      // designer-side testing
  'tunifydesign@gmail.com', // shop-side testing
];

/** Whether this signed-in user is the test account. */
export function isTestAccount(user) {
  return Boolean(user?.email)
    && TEST_ACCOUNT_EMAILS.includes(user.email.toLowerCase())
    && user.emailVerified !== false;
}

// ── SHOPS SELL SOMETHING ELSE, SO THEY BUY SOMETHING ELSE ────────────────────
// A SEPARATE LADDER, not the designer one relabelled. The two differ in what
// they contain (product images against a portfolio, recommended SHOPS against
// recommended designers), in how many rungs they have — shops have no bronze —
// and in price. Bending one table to cover both would mean a `bronze` a shop can
// never buy and a `portfolioImages` that means nothing to it.
//
// The FIELD on users/{uid} is the same `plan`, holding one of SHOP_PLAN_IDS.
// `userType` already says which ladder to read it against, so nothing new is
// stored and no document can be ambiguous: a shop on 'silver' is on shop-silver.
//
// ⚠️ 'bronze' IS NOT A SHOP TIER. planOfShop falls back to free for it, so a
// designer account converted to a shop — or a stale value — degrades to the
// free tier rather than inheriting a plan that does not exist here.
export const SHOP_PLAN_IDS = ['free', 'silver', 'gold'];

export const SHOP_PLANS = {
  free: {
    id: 'free',
    name: 'Free',
    priceUsd: 0,
    // Per listing, not per shop: 30 is generous for one product and a real
    // ceiling for a catalogue built on many.
    productImages: 30,
    searchRank: 0,
    // 'none' | 'chance' | 'always'. Three states because the rail is RANKED and
    // partly random (see RecommendationsFeed): silver competes for a place,
    // gold is guaranteed one. A boolean could not tell those apart.
    recommended: 'none',
    pageStyle: 'default',
    landingPage: false,
    // Days of promotion on the Tunify Instagram and TikTok. A DURATION, not a
    // count of posts, which is how the shop tiers were specified.
    socialDays: 0,
    announcements: 0,
    partner: false,
  },
  silver: {
    id: 'silver',
    name: 'Silver',
    priceUsd: 9.99,
    productImages: Infinity,
    searchRank: 2,
    recommended: 'chance',
    pageStyle: 'silver',
    landingPage: false,
    socialDays: 2,
    announcements: 1,
    partner: false,
    popular: true,
  },
  gold: {
    id: 'gold',
    name: 'Gold',
    priceUsd: 19.99,
    productImages: Infinity,
    searchRank: 3,
    recommended: 'always',
    pageStyle: 'gold',
    landingPage: true,
    socialDays: 4,
    announcements: 3,
    // A MARK ON THE PROFILE, not an entitlement — nothing reads this except the
    // chip beside the shop's name (see TIER_CAPS in TierMarks.jsx). A field
    // rather than an `id === 'gold'` test in that file, so the plans table stays
    // the one place that says what a tier includes.
    partner: true,
  },
};

/** The shop plan record for a raw value or a user document. Unknown → free. */
export function planOfShop(user) {
  const id = typeof user === 'string' ? user : user?.plan;
  return SHOP_PLANS[id] || SHOP_PLANS.free;
}

/**
 * The right ladder for an account, chosen by `userType`.
 *
 * One entry point so no caller has to remember which of the two tables applies
 * — and so a shop can never be measured against a designer's tier by accident.
 */
export function planForUser(user) {
  return user?.userType === 'shop' ? planOfShop(user) : planOf(user);
}
